import { useState, useEffect, useCallback } from "react";
import { adminCsrfHeaders } from "../lib/adminApi";
import { useLocation, useParams, Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { getPlanBadgeStyle } from "@/lib/planBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  LogOut,
  ArrowLeft,
  Building2,
  Users,
  CreditCard,
  ScrollText,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileText,
  BookOpen,
  ShieldAlert,
  Bell,
  Paperclip,
  UserCircle2,
  UserCog,
} from "lucide-react";

// UI hide, NOT a feature removal. The per-org feature-flags panel writes to
// orgFeatureFlagsTable, but the outstanding-work audit found no code reads
// those flags — toggling them does nothing and misleads admins. Keep the panel
// code + table + migrations intact; just stop rendering the panel until
// consumers are wired. Flip to true to restore the UI.
const SHOW_FEATURE_FLAGS_PANEL = false;

interface OrgContractsResponse {
  total: number;
  contracts: {
    id: number;
    contractType: string;
    status: string;
    lifecycleState: string;
    documentKind: string;
    startDate: string | null;
    endDate: string | null;
    createdAt: string;
    coachName: string;
    coachSport: string | null;
  }[];
}

interface OrgClausesResponse {
  total: number;
  seedTotal: number;
  customTotal: number;
  clauses: {
    id: number;
    title: string;
    category: string;
    jurisdiction: string | null;
    documentKind: string;
    isSeed: boolean;
    updatedAt: string;
  }[];
}

interface OrgDetail {
  org: {
    id: number;
    name: string;
    institutionName: string | null;
    plan: string;
    division: string | null;
    conference: string | null;
    stripeCustomerId: string | null;
    stripeSubscriptionId: string | null;
    stripeCurrentPeriodEnd: string | null;
    emailAlertsEnabled: boolean;
    allowedSigningMethods: string;
    alertEmail: string | null;
    status?: string;
    suspendedAt?: string | null;
    suspendedReason?: string | null;
    createdAt: string;
    updatedAt: string;
  };
  members: {
    id: number;
    email: string;
    role: string;
    invitedAt: string;
    acceptedAt: string | null;
    status: string;
    personas?: string[] | null;
  }[];
  stripeSubscription: {
    id: string;
    status: string;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    plan: string | null;
    priceAmount: number | null;
  } | null;
  auditLog: {
    id: number;
    entityType: string;
    entityId: number;
    action: string;
    field: string | null;
    oldValue: string | null;
    newValue: string | null;
    actorName: string | null;
    createdAt: string;
  }[];
}

function fmtDate(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function fmtDateTime(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function planBadge(plan: string) {
  return getPlanBadgeStyle(plan).className;
}

function planLabel(plan: string) {
  return getPlanBadgeStyle(plan).label;
}

function stripeStatusBadge(status: string) {
  if (status === "active") return "bg-green-900/40 text-green-300 border border-green-700/50";
  if (status === "trialing") return "bg-yellow-900/40 text-yellow-300 border border-yellow-700/50";
  if (status === "canceled" || status === "cancelled") return "bg-red-900/40 text-red-300 border border-red-700/50";
  return "bg-gray-700/50 text-gray-400 border border-gray-600/50";
}

function actionBadge(action: string) {
  if (action === "create") return "text-green-400";
  if (action === "delete") return "text-red-400";
  if (action === "status_change") return "text-purple-400";
  return "text-blue-400";
}

// Owner-only management panel: personas, impersonation, seat-cap, flags.
interface OwnerControlsProps {
  orgId: number;
  members: { id: number; email: string; role: string; status: string; personas?: string[] | null }[];
}

const PERSONA_OPTIONS = ["Finance", "HR", "Legal", "AD", "SA"] as const;

// Catalog of known per-org flags rendered as toggles. Custom keys can still
// be added via the "Custom flag" row below the list.
const KNOWN_FLAGS: { key: string; label: string }[] = [
  { key: "early_access", label: "Early access — surface beta features to this org" },
  { key: "advanced_compliance", label: "Advanced compliance — extra policy_rules + clause checks" },
  { key: "ai_negotiation_assist", label: "AI negotiation assist — drafting helpers in negotiation panels" },
  { key: "bulk_pdf_import", label: "Bulk PDF import — multi-file extract & ingest pipeline" },
  { key: "owner_support_mode", label: "Owner support mode — extra diagnostics in the org's own UI" },
];

function OwnerControls({ orgId, members }: OwnerControlsProps) {
  const { toast } = useToast();
  const [seatCap, setSeatCap] = useState<string>("");
  const [flagKey, setFlagKey] = useState("");
  const [flagEnabled, setFlagEnabled] = useState(true);
  const [flags, setFlags] = useState<{ flagKey: string; enabled: boolean; reason: string | null; updatedAt: string }[]>([]);
  const [memberPersonas, setMemberPersonas] = useState<Record<number, string[]>>(() => {
    const init: Record<number, string[]> = {};
    for (const m of members) init[m.id] = Array.isArray(m.personas) ? m.personas : [];
    return init;
  });
  const [impersonateBusy, setImpersonateBusy] = useState(false);

  useEffect(() => {
    const init: Record<number, string[]> = {};
    for (const m of members) init[m.id] = Array.isArray(m.personas) ? m.personas : [];
    setMemberPersonas(init);
  }, [members]);

  useEffect(() => {
    void (async () => {
      try {
        const res = await fetch(`/api/admin/orgs/${orgId}/feature-flags`, { credentials: "include" });
        if (res.ok) setFlags(((await res.json()) as { flags: typeof flags }).flags);
      } catch {/* ignore */}
    })();
  }, [orgId]);

  async function savePersonas(memberId: number) {
    const personas = memberPersonas[memberId] ?? [];
    try {
      const res = await fetch(`/api/admin/orgs/${orgId}/members/${memberId}/personas`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...adminCsrfHeaders() },
        body: JSON.stringify({ personas }),
      });
      if (res.ok) toast({ title: "Personas saved", description: personas.length ? personas.join(", ") : "(none)" });
      else toast({ title: "Failed to save personas", variant: "destructive" });
    } catch {
      toast({ title: "Failed to save personas", variant: "destructive" });
    }
  }

  async function startImpersonate(mode: "read_only" | "full_access") {
    const reason = window.prompt(
      mode === "full_access"
        ? "FULL ACCESS impersonation — every change you make is real and is audited as you. Reason (required, audited):"
        : "Read-only impersonation. Reason (required, audited):",
    ) ?? "";
    if (!reason.trim()) return;
    // Task #484 (review #2): full_access writes get attributed to the
    // operator's email instead of an opaque "owner_admin" tag, so we have
    // to ask the operator to self-attest. Persisted on the impersonation
    // session row + surfaced in every customer-facing audit row.
    let ownerEmail: string | undefined;
    if (mode === "full_access") {
      const raw = window.prompt(
        "Your owner email (required, audited as the operator behind every write):",
      ) ?? "";
      const trimmed = raw.trim();
      if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        toast({
          title: "Operator email required",
          description: "Enter a valid email address to start full-access impersonation.",
          variant: "destructive",
        });
        return;
      }
      ownerEmail = trimmed;
      const ok = window.confirm(
        `Confirm FULL ACCESS impersonation of this org. Writes will succeed and be tagged "${ownerEmail} (impersonating <org>)" in the customer's audit log.\n\nReason: ${reason.trim()}`,
      );
      if (!ok) return;
    }
    setImpersonateBusy(true);
    try {
      const res = await fetch(`/api/admin/orgs/${orgId}/impersonate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...adminCsrfHeaders() },
        body: JSON.stringify({ reason: reason.trim(), mode, ownerEmail }),
      });
      if (res.ok) {
        // Drop any React Query data the owner accumulated for their own org
        // before we reload as the impersonated tenant (task #453).
        queryClient.clear();
        // Skip HomeRedirect's persona branching entirely — the impersonated
        // viewer has personas:[] which sometimes routes to /onboarding while
        // OrgProvider is still re-fetching. /dashboard is always safe.
        const base = import.meta.env.BASE_URL.replace(/\/$/, "");
        window.location.href = `${base}/dashboard`;
      } else {
        let description: string | undefined;
        try {
          const body = (await res.json()) as { code?: string; message?: string; error?: string };
          description = body.message ?? body.code ?? body.error;
        } catch {/* non-JSON body */}
        toast({
          title: "Failed to start impersonation",
          description: description ?? `Server returned ${res.status}`,
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Failed to start impersonation",
        description: err instanceof Error ? err.message : "Network error",
        variant: "destructive",
      });
    } finally {
      setImpersonateBusy(false);
    }
  }

  async function setSeatCapOverride() {
    const value = seatCap.trim() === "" ? null : Number(seatCap);
    if (value !== null && (!Number.isFinite(value) || value < 0)) {
      toast({ title: "Invalid seat-cap", description: "Enter a non-negative integer or leave blank to clear.", variant: "destructive" });
      return;
    }
    const res = await fetch(`/api/admin/orgs/${orgId}/seat-cap`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...adminCsrfHeaders() },
      body: JSON.stringify({ seatCapOverride: value }),
    });
    if (res.ok) toast({ title: "Seat-cap saved", description: value === null ? "Cleared override" : `Override = ${value}` });
    else toast({ title: "Failed to save seat-cap", variant: "destructive" });
  }

  async function refreshFlags() {
    const list = await fetch(`/api/admin/orgs/${orgId}/feature-flags`, { credentials: "include" });
    if (list.ok) setFlags(((await list.json()) as { flags: typeof flags }).flags);
  }

  async function toggleFlag(key: string, enabled: boolean) {
    const res = await fetch(`/api/admin/orgs/${orgId}/feature-flags/${encodeURIComponent(key)}`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...adminCsrfHeaders() },
      body: JSON.stringify({ enabled }),
    });
    if (res.ok) {
      await refreshFlags();
      toast({ title: enabled ? "Flag enabled" : "Flag disabled", description: key });
    } else {
      toast({ title: "Failed to toggle flag", description: key, variant: "destructive" });
    }
  }

  async function upsertFlag() {
    if (!flagKey.trim()) return;
    const savedKey = flagKey.trim();
    const res = await fetch(`/api/admin/orgs/${orgId}/feature-flags/${encodeURIComponent(savedKey)}`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...adminCsrfHeaders() },
      body: JSON.stringify({ enabled: flagEnabled }),
    });
    if (res.ok) {
      setFlagKey("");
      await refreshFlags();
      toast({ title: "Feature flag saved", description: `${savedKey} = ${flagEnabled ? "on" : "off"}` });
    } else {
      toast({ title: "Failed to save flag", variant: "destructive" });
    }
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-5">
      <h2 className="text-sm font-semibold text-white">Owner Controls</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-gray-500 mb-1">Impersonation</p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => void startImpersonate("read_only")}
              disabled={impersonateBusy}
              className="px-3 py-1.5 text-sm rounded bg-yellow-700 hover:bg-yellow-600 disabled:opacity-60 text-white"
              data-testid="impersonate-read-only"
            >
              {impersonateBusy ? "Starting…" : "View only"}
            </button>
            <button
              onClick={() => void startImpersonate("full_access")}
              disabled={impersonateBusy}
              className="px-3 py-1.5 text-sm rounded bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white"
              data-testid="impersonate-full-access"
            >
              {impersonateBusy ? "Starting…" : "Full access"}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            View only: all writes blocked. Full access: writes succeed and are audited as you.
          </p>
        </div>

        <div>
          <p className="text-xs text-gray-500 mb-1">Seat-cap override</p>
          <div className="flex gap-2 items-center">
            <input
              type="number"
              min={0}
              value={seatCap}
              onChange={(e) => setSeatCap(e.target.value)}
              placeholder="blank = use plan default"
              className="px-2 py-1 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100 w-44"
            />
            <button onClick={setSeatCapOverride} className="px-3 py-1 text-sm rounded bg-gray-700 hover:bg-gray-600 text-white">Save</button>
          </div>
        </div>
      </div>

      <div>
        <p className="text-xs text-gray-500 mb-2">Per-member personas</p>
        <div className="space-y-1.5 max-h-60 overflow-auto pr-1">
          {members.map((m) => (
            <div key={m.id} className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-gray-300 w-56 truncate">{m.email}</span>
              {PERSONA_OPTIONS.map((p) => {
                const cur = memberPersonas[m.id] ?? [];
                const checked = cur.includes(p);
                return (
                  <label key={p} className="flex items-center gap-1 text-gray-400">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const next = e.target.checked ? [...cur, p] : cur.filter((x) => x !== p);
                        setMemberPersonas((s) => ({ ...s, [m.id]: next }));
                      }}
                    />
                    {p}
                  </label>
                );
              })}
              <button onClick={() => void savePersonas(m.id)} className="px-2 py-0.5 rounded bg-gray-700 hover:bg-gray-600 text-white">Save</button>
            </div>
          ))}
        </div>
      </div>

      {SHOW_FEATURE_FLAGS_PANEL && (
      <div>
        <p className="text-xs text-gray-500 mb-2">Feature flags</p>
        <p className="text-[11px] text-gray-500 mb-2">
          Toggle a known flag, or use the "custom" row at the bottom to add a new one. All changes audited.
        </p>
        <ul className="text-xs divide-y divide-gray-800 mb-3">
          {KNOWN_FLAGS.map((known) => {
            const cur = flags.find((f) => f.flagKey === known.key);
            const enabled = cur?.enabled ?? false;
            return (
              <li key={known.key} className="py-2 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-gray-200">{known.label}</p>
                  <p className="text-[11px] text-gray-500 truncate">
                    <code className="bg-gray-800 px-1 rounded">{known.key}</code>
                    {cur ? ` · updated ${new Date(cur.updatedAt).toLocaleDateString()}` : " · not set"}
                  </p>
                </div>
                <label className="inline-flex items-center cursor-pointer gap-2">
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => void toggleFlag(known.key, e.target.checked)}
                    className="h-4 w-4"
                  />
                  <span className={enabled ? "text-green-400" : "text-gray-500"}>{enabled ? "ON" : "off"}</span>
                </label>
              </li>
            );
          })}
          {flags
            .filter((f) => !KNOWN_FLAGS.some((k) => k.key === f.flagKey))
            .map((f) => (
              <li key={f.flagKey} className="py-2 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-gray-200 truncate">{f.flagKey}</p>
                  <p className="text-[11px] text-gray-500">custom · updated {new Date(f.updatedAt).toLocaleDateString()}</p>
                </div>
                <label className="inline-flex items-center cursor-pointer gap-2">
                  <input
                    type="checkbox"
                    checked={f.enabled}
                    onChange={(e) => void toggleFlag(f.flagKey, e.target.checked)}
                    className="h-4 w-4"
                  />
                  <span className={f.enabled ? "text-green-400" : "text-gray-500"}>{f.enabled ? "ON" : "off"}</span>
                </label>
              </li>
            ))}
        </ul>
        <div className="flex flex-wrap items-center gap-2 text-xs border-t border-gray-800 pt-3">
          <span className="text-gray-500">Custom flag:</span>
          <input
            value={flagKey}
            onChange={(e) => setFlagKey(e.target.value)}
            placeholder="flag_key"
            className="px-2 py-1 bg-gray-800 border border-gray-700 rounded text-gray-100 w-48"
          />
          <label className="flex items-center gap-1 text-gray-400">
            <input type="checkbox" checked={flagEnabled} onChange={(e) => setFlagEnabled(e.target.checked)} />
            enabled
          </label>
          <button onClick={() => void upsertFlag()} className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white">Add / update</button>
        </div>
      </div>
      )}
    </div>
  );
}

interface OrgPersonasResponse {
  orgId: number;
  memberCount: number;
  windowDays: number;
  distribution: {
    persona: string;
    assignedMembers: number;
    activeMembers30d: number;
    dormantMembers: number;
    events30d: number;
  }[];
  recentActivity: {
    id: number;
    createdAt: string;
    actorName: string | null;
    actorPersonas: string[];
    entityType: string;
    entityId: number;
    field: string;
    action: string;
  }[];
}

function OrgPersonasCard({ orgId }: { orgId: number }) {
  const [data, setData] = useState<OrgPersonasResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/admin/orgs/${orgId}/personas`, { credentials: "include" });
        if (!res.ok) {
          if (!cancelled) setError(`Failed to load personas (${res.status})`);
          return;
        }
        const json = (await res.json()) as OrgPersonasResponse;
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Network error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden" data-testid="org-personas-card">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-800">
        <UserCog className="h-4 w-4 text-indigo-400" />
        <h2 className="text-sm font-semibold text-white">Personas</h2>
        {data && (
          <span className="text-xs text-gray-500 ml-auto">
            {data.memberCount} member{data.memberCount === 1 ? "" : "s"} · activity last {data.windowDays}d
          </span>
        )}
      </div>
      {loading ? (
        <p className="px-5 py-4 text-sm text-gray-500">Loading…</p>
      ) : error || !data ? (
        <p className="px-5 py-4 text-sm text-red-400">{error ?? "No data."}</p>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                {["Persona", "Assigned", "Active 30d", "Dormant", "Events 30d"].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.distribution.map((p) => (
                <tr key={p.persona} className="border-b border-gray-800/50">
                  <td className="px-4 py-2.5 text-gray-200 font-medium">{p.persona}</td>
                  <td className="px-4 py-2.5 text-gray-300 tabular-nums">{p.assignedMembers}</td>
                  <td className="px-4 py-2.5 text-green-400 tabular-nums">{p.activeMembers30d}</td>
                  <td className={`px-4 py-2.5 tabular-nums ${p.dormantMembers > 0 ? "text-yellow-400" : "text-gray-500"}`}>{p.dormantMembers}</td>
                  <td className="px-4 py-2.5 text-gray-300 tabular-nums">{p.events30d}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.recentActivity.length > 0 && (
            <div className="border-t border-gray-800">
              <div className="px-5 py-3 text-xs text-gray-500 uppercase tracking-wide">Recent persona-attributed changes</div>
              <ul className="divide-y divide-gray-800/60">
                {data.recentActivity.slice(0, 10).map((a) => (
                  <li key={a.id} className="px-5 py-2 text-xs text-gray-300 flex flex-wrap items-center gap-2">
                    <span className="text-gray-500">{new Date(a.createdAt).toLocaleString()}</span>
                    <span className="text-gray-200">{a.actorName ?? "(unknown)"}</span>
                    <span className="text-gray-400">{a.action} <code className="text-gray-200">{a.field}</code> on {a.entityType} #{a.entityId}</span>
                    <span className="ml-auto flex gap-1">
                      {a.actorPersonas.map((p) => (
                        <span key={p} className="px-1.5 py-0.5 rounded bg-indigo-900/40 border border-indigo-700/50 text-indigo-300">{p}</span>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function AdminOrgDetail() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const params = useParams<{ id: string }>();
  const [data, setData] = useState<OrgDetail | null>(null);
  const [contractsData, setContractsData] = useState<OrgContractsResponse | null>(null);
  const [clausesData, setClausesData] = useState<OrgClausesResponse | null>(null);
  const [coaches, setCoaches] = useState<{ id: number; firstName: string; lastName: string; sport: string | null; title: string | null; status: string }[]>([]);
  const [files, setFiles] = useState<{ id: number; contractId: number; fileName: string; mimeType: string; sizeBytes: number; uploadedAt: string }[]>([]);
  const [notifs, setNotifs] = useState<{ id: number; type: string; title: string; createdAt: string; readAt: string | null }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!params.id) return;
    setLoading(true);
    setError(null);
    try {
      const [resDetail, resContracts, resClauses, resCoaches, resFiles, resNotifs] = await Promise.all([
        fetch(`/api/admin/orgs/${params.id}`, { credentials: "include" }),
        fetch(`/api/admin/orgs/${params.id}/contracts`, { credentials: "include" }),
        fetch(`/api/admin/orgs/${params.id}/clauses`, { credentials: "include" }),
        fetch(`/api/admin/orgs/${params.id}/coaches`, { credentials: "include" }),
        fetch(`/api/admin/orgs/${params.id}/files`, { credentials: "include" }),
        fetch(`/api/admin/orgs/${params.id}/notifications`, { credentials: "include" }),
      ]);
      if (resDetail.status === 401) { setLocation("/admin/login"); return; }
      if (resDetail.status === 404) { setError("Organization not found"); setLoading(false); return; }
      const d = await resDetail.json() as OrgDetail;
      setData(d);
      if (resContracts.ok) setContractsData(await resContracts.json() as OrgContractsResponse);
      if (resClauses.ok) setClausesData(await resClauses.json() as OrgClausesResponse);
      if (resCoaches.ok) setCoaches(((await resCoaches.json()) as { coaches: typeof coaches }).coaches);
      if (resFiles.ok) setFiles(((await resFiles.json()) as { files: typeof files }).files);
      if (resNotifs.ok) setNotifs(((await resNotifs.json()) as { notifications: typeof notifs }).notifications);
    } catch {
      setError("Failed to load organization details.");
    } finally {
      setLoading(false);
    }
  }, [params.id, setLocation]);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function handleLogout() {
    await fetch("/api/admin/logout", { method: "POST", credentials: "include", headers: adminCsrfHeaders() });
    setLocation("/admin/login");
  }

  async function handleSuspend() {
    if (!params.id) return;
    const reason = window.prompt("Reason for suspending this organization (optional):") ?? "";
    if (!window.confirm(`Suspend ${data?.org.institutionName ?? data?.org.name}? All members will lose access until reactivation.`)) return;
    const res = await fetch(`/api/admin/orgs/${params.id}/suspend`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...adminCsrfHeaders() },
      body: JSON.stringify({ reason }),
    });
    if (res.ok) await fetchData();
    else alert("Failed to suspend org");
  }

  async function handleReactivate() {
    if (!params.id) return;
    if (!window.confirm("Reactivate this organization?")) return;
    const res = await fetch(`/api/admin/orgs/${params.id}/reactivate`, {
      method: "POST",
      credentials: "include",
      headers: adminCsrfHeaders(),
    });
    if (res.ok) await fetchData();
    else alert("Failed to reactivate org");
  }

  async function handleResetRole(memberId: number) {
    if (!params.id) return;
    if (!window.confirm("Reset this member's role to 'member'? This is audited.")) return;
    const res = await fetch(`/api/admin/orgs/${params.id}/members/${memberId}/reset-role`, {
      method: "POST",
      credentials: "include",
      headers: adminCsrfHeaders(),
    });
    if (res.ok) {
      toast({ title: "Member role reset", description: "Role set to 'member'." });
      await fetchData();
    } else {
      toast({ title: "Failed to reset role", variant: "destructive" });
    }
  }

  async function handleRevokeInvite(memberId: number) {
    if (!params.id) return;
    if (!window.confirm("Revoke this pending invitation? It will be removed and audited.")) return;
    const res = await fetch(`/api/admin/orgs/${params.id}/invitations/${memberId}/revoke`, {
      method: "POST",
      credentials: "include",
      headers: adminCsrfHeaders(),
    });
    if (res.ok) {
      toast({ title: "Invitation revoked" });
      await fetchData();
    } else {
      toast({ title: "Failed to revoke invitation", variant: "destructive" });
    }
  }

  async function handleCancelSubscription() {
    if (!params.id) return;
    if (!window.confirm("Cancel this org's Stripe subscription at period end?")) return;
    const res = await fetch(`/api/admin/orgs/${params.id}/subscription/cancel`, {
      method: "POST",
      credentials: "include",
      headers: adminCsrfHeaders(),
    });
    if (res.ok) {
      toast({ title: "Subscription scheduled for cancellation" });
      await fetchData();
    } else {
      toast({ title: "Failed to cancel subscription", variant: "destructive" });
    }
  }

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <img src="/logo.svg" alt="Zindeal" className="w-7 h-7 rounded-full" />
          <span className="font-bold text-sm">Zindeal</span>
          <span className="text-gray-600 text-sm mx-1">/</span>
          <Link href="/admin" className="text-gray-400 text-sm hover:text-white transition-colors">Owner Console</Link>
          <span className="text-gray-600 text-sm mx-1">/</span>
          <span className="text-gray-300 text-sm truncate max-w-[200px]">{data?.org.institutionName ?? data?.org.name ?? "Organization"}</span>
          <div className="ml-auto">
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white px-2.5 py-1.5 rounded-md hover:bg-gray-800 transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <Link href="/admin" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to Organizations
        </Link>

        {loading && (
          <div className="text-center py-16 text-gray-500">Loading organization details…</div>
        )}
        {error && (
          <div className="text-center py-16">
            <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-3" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {data && (
          <div className="space-y-5">
            <div className="bg-red-950/40 border border-red-800/60 rounded-lg px-4 py-2.5 flex items-center gap-2.5">
              <ShieldAlert className="h-4 w-4 text-red-400 flex-shrink-0" />
              <p className="text-xs text-red-200">
                <span className="font-semibold">Owner view — Org: {data.org.institutionName ?? data.org.name}.</span> You are viewing another organization's data as the platform owner. Treat all information as confidential.
              </p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-gray-300 mr-2">Owner actions:</span>
              {data.org.status === "suspended" ? (
                <>
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-900/40 text-red-300 border border-red-700/50">
                    Suspended{data.org.suspendedReason ? ` — ${data.org.suspendedReason}` : ""}
                  </span>
                  <button
                    onClick={handleReactivate}
                    className="text-xs px-3 py-1.5 rounded-md bg-green-900/40 text-green-300 border border-green-700/50 hover:bg-green-900/60"
                  >
                    Reactivate org
                  </button>
                </>
              ) : (
                <button
                  onClick={handleSuspend}
                  className="text-xs px-3 py-1.5 rounded-md bg-red-900/40 text-red-300 border border-red-700/50 hover:bg-red-900/60"
                >
                  Suspend org
                </button>
              )}
              {data.org.stripeSubscriptionId && (
                <button
                  onClick={handleCancelSubscription}
                  className="text-xs px-3 py-1.5 rounded-md bg-yellow-900/40 text-yellow-300 border border-yellow-700/50 hover:bg-yellow-900/60"
                >
                  Cancel subscription
                </button>
              )}
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#0B1834]/30 border border-[#0B1834]/50 flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-6 w-6 text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-xl font-bold text-white">{data.org.institutionName ?? data.org.name}</h1>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${planBadge(data.org.plan)}`}>
                      {planLabel(data.org.plan)}
                    </span>
                  </div>
                  {(data.org.division || data.org.conference) && (
                    <p className="text-sm text-gray-400 mt-0.5">
                      {[data.org.division, data.org.conference].filter(Boolean).join(" · ")}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Org ID: {data.org.id} · Joined {fmtDate(data.org.createdAt)}</p>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-4 pt-4 border-t border-gray-800">
                {[
                  { label: "Alert Email", value: data.org.alertEmail ?? "—" },
                  { label: "Email Alerts", value: data.org.emailAlertsEnabled ? "Enabled" : "Disabled" },
                  { label: "Signing Methods", value: data.org.allowedSigningMethods.replace(/_/g, " ") },
                  { label: "Stripe Customer ID", value: data.org.stripeCustomerId ?? "—" },
                  { label: "Subscription ID", value: data.org.stripeSubscriptionId ?? "—" },
                  { label: "Period End", value: fmtDate(data.org.stripeCurrentPeriodEnd) },
                ].map((f) => (
                  <div key={f.label}>
                    <p className="text-xs text-gray-500">{f.label}</p>
                    <p className="text-sm text-gray-200 mt-0.5 truncate">{f.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {data.stripeSubscription && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="h-4 w-4 text-gray-400" />
                  <h2 className="text-sm font-semibold text-white">Stripe Subscription</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Status</p>
                    <span className={`mt-0.5 inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize ${stripeStatusBadge(data.stripeSubscription.status)}`}>
                      {data.stripeSubscription.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Plan / Price</p>
                    <p className="text-sm text-gray-200 mt-0.5">
                      {data.stripeSubscription.plan ?? "—"}
                      {data.stripeSubscription.priceAmount != null && (
                        <span className="text-gray-400"> (${(data.stripeSubscription.priceAmount / 100).toFixed(0)}/mo)</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Period End</p>
                    <p className="text-sm text-gray-200 mt-0.5">{fmtDate(data.stripeSubscription.currentPeriodEnd)}</p>
                  </div>
                  {data.stripeSubscription.cancelAtPeriodEnd && (
                    <div className="col-span-full">
                      <span className="text-xs text-yellow-400 bg-yellow-950/40 border border-yellow-800 rounded-lg px-3 py-1.5 inline-block">
                        Cancels at period end
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <OwnerControls orgId={parseInt(params.id, 10)} members={data.members} />

            <OrgPersonasCard orgId={parseInt(params.id, 10)} />

            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-800">
                <Users className="h-4 w-4 text-gray-400" />
                <h2 className="text-sm font-semibold text-white">Team Members ({data.members.length})</h2>
              </div>
              {data.members.length === 0 ? (
                <p className="px-5 py-4 text-sm text-gray-500">No members found.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800">
                      {["Email", "Role", "Personas", "Status", "Invited", "Joined", "Actions"].map((h) => (
                        <th key={h} className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.members.map((m) => (
                      <tr key={m.id} className="border-b border-gray-800/50 hover:bg-gray-800/20">
                        <td className="px-4 py-2.5 text-gray-200">{m.email}</td>
                        <td className="px-4 py-2.5">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${m.role === "admin" ? "bg-blue-900/40 text-blue-300 border border-blue-700/50" : "bg-gray-700/50 text-gray-400 border border-gray-600/50"}`}>
                            {m.role}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-300">
                          {Array.isArray(m.personas) && m.personas.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {m.personas.map((p) => (
                                <span key={p} className="px-1.5 py-0.5 rounded bg-gray-800 border border-gray-700 text-gray-300">{p}</span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-600">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          {m.status === "active"
                            ? <span className="flex items-center gap-1 text-green-400 text-xs"><CheckCircle2 className="h-3 w-3" />Active</span>
                            : <span className="flex items-center gap-1 text-yellow-400 text-xs"><Clock className="h-3 w-3" />Pending</span>
                          }
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-400">{fmtDate(m.invitedAt)}</td>
                        <td className="px-4 py-2.5 text-xs text-gray-400">{fmtDate(m.acceptedAt)}</td>
                        <td className="px-4 py-2.5 text-xs">
                          <div className="flex flex-wrap items-center gap-1.5">
                            {m.role !== "member" && m.status === "active" && (
                              <button
                                onClick={() => void handleResetRole(m.id)}
                                className="px-2 py-0.5 rounded bg-blue-900/40 text-blue-300 border border-blue-700/50 hover:bg-blue-900/60"
                              >
                                Reset role
                              </button>
                            )}
                            {m.status !== "active" && (
                              <button
                                onClick={() => void handleRevokeInvite(m.id)}
                                className="px-2 py-0.5 rounded bg-red-900/40 text-red-300 border border-red-700/50 hover:bg-red-900/60"
                              >
                                Revoke invite
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {contractsData && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-800">
                  <FileText className="h-4 w-4 text-gray-400" />
                  <h2 className="text-sm font-semibold text-white">
                    Contracts &amp; Term Sheets ({contractsData.total})
                  </h2>
                  {contractsData.contracts.length < contractsData.total && (
                    <span className="text-xs text-gray-500 ml-auto">showing latest {contractsData.contracts.length}</span>
                  )}
                </div>
                {contractsData.contracts.length === 0 ? (
                  <p className="px-5 py-4 text-sm text-gray-500">No contracts in this organization yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-800">
                          {["Coach", "Sport", "Type", "Kind", "Status", "Stage", "Effective", "Expires"].map((h) => (
                            <th key={h} className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {contractsData.contracts.map((c) => (
                          <tr key={c.id} className="border-b border-gray-800/50 hover:bg-gray-800/20">
                            <td className="px-4 py-2.5 text-gray-200">{c.coachName}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-400">{c.coachSport ?? "—"}</td>
                            <td className="px-4 py-2.5 text-xs text-blue-400 hover:underline capitalize">
                              <Link href={`/admin/orgs/${params.id}/contracts/${c.id}`}>
                                {c.contractType.replace(/_/g, " ")}
                              </Link>
                            </td>
                            <td className="px-4 py-2.5">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${c.documentKind === "term_sheet" ? "bg-purple-900/40 text-purple-300 border border-purple-700/50" : "bg-blue-900/40 text-blue-300 border border-blue-700/50"}`}>
                                {c.documentKind.replace("_", " ")}
                              </span>
                            </td>
                            <td className="px-4 py-2.5">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${c.status === "active" ? "bg-green-900/40 text-green-300 border border-green-700/50" : c.status === "expired" || c.status === "terminated" ? "bg-red-900/40 text-red-300 border border-red-700/50" : "bg-gray-700/50 text-gray-400 border border-gray-600/50"}`}>
                                {c.status}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-gray-400 capitalize">{c.lifecycleState.replace(/_/g, " ")}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-400">{fmtDate(c.startDate)}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-400">{fmtDate(c.endDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {coaches.length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-800">
                  <UserCircle2 className="h-4 w-4 text-gray-400" />
                  <h2 className="text-sm font-semibold text-white">Coaches ({coaches.length})</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-800">
                        {["Name", "Sport", "Title", "Email", "Status"].map((h) => (
                          <th key={h} className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {coaches.map((c) => (
                        <tr key={c.id} className="border-b border-gray-800/50 hover:bg-gray-800/20">
                          <td className="px-4 py-2.5 text-blue-400 hover:underline">
                            <Link href={`/admin/orgs/${params.id}/coaches/${c.id}`}>
                              {c.firstName} {c.lastName}
                            </Link>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-gray-400">{c.sport ?? "—"}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-400">{c.title ?? "—"}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-400 truncate max-w-xs">{(c as { email?: string }).email ?? "—"}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-300 capitalize">{c.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <Tabs defaultValue="files" className="w-full">
                <TabsList className="m-3">
                  <TabsTrigger value="files" className="text-xs">
                    <Paperclip className="h-3.5 w-3.5 mr-1.5" />
                    Files ({files.length})
                  </TabsTrigger>
                  <TabsTrigger value="notifications" className="text-xs">
                    <Bell className="h-3.5 w-3.5 mr-1.5" />
                    Notifications ({notifs.length})
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="files" className="mt-0">
                  {files.length === 0 ? (
                    <p className="px-5 py-6 text-sm text-gray-500 text-center">No uploaded files for this organization.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-y border-gray-800">
                          {["File", "Type", "Size", "Contract", "Uploaded", "Actions"].map((h) => (
                            <th key={h} className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {files.map((f) => (
                          <tr key={f.id} className="border-b border-gray-800/50 hover:bg-gray-800/20">
                            <td className="px-4 py-2.5 text-gray-200 truncate max-w-xs">{f.fileName}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-400">{f.mimeType}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-400">{(f.sizeBytes / 1024).toFixed(1)} KB</td>
                            <td className="px-4 py-2.5 text-xs text-blue-400 hover:underline">
                              <Link href={`/admin/orgs/${params.id}/contracts/${f.contractId}`}>#{f.contractId}</Link>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-gray-400">{fmtDate(f.uploadedAt)}</td>
                            <td className="px-4 py-2.5 text-xs">
                              <a
                                href={`/api/admin/orgs/${params.id}/files/${f.id}/download`}
                                className="px-2 py-0.5 rounded bg-gray-700 hover:bg-gray-600 text-white"
                              >
                                Download
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </TabsContent>
                <TabsContent value="notifications" className="mt-0">
                  {notifs.length === 0 ? (
                    <p className="px-5 py-6 text-sm text-gray-500 text-center">No notifications recorded for this organization.</p>
                  ) : (
                    <ul className="divide-y divide-gray-800/50 text-xs border-t border-gray-800">
                      {notifs.slice(0, 30).map((n) => (
                        <li key={n.id} className="px-5 py-2 flex items-center gap-3">
                          <span className="px-1.5 py-0.5 rounded bg-gray-800 text-gray-300">{n.type}</span>
                          <span className="text-gray-200 flex-1 truncate">{n.title}</span>
                          <span className={n.readAt ? "text-gray-500" : "text-yellow-400"}>{n.readAt ? "read" : "unread"}</span>
                          <span className="text-gray-500">{fmtDate(n.createdAt)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </TabsContent>
              </Tabs>
            </div>

            {clausesData && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-800">
                  <BookOpen className="h-4 w-4 text-gray-400" />
                  <h2 className="text-sm font-semibold text-white">
                    Clause Library ({clausesData.total})
                  </h2>
                  <span className="text-xs text-gray-500 ml-auto">
                    {clausesData.customTotal} custom · {clausesData.seedTotal} starter
                  </span>
                </div>
                {clausesData.clauses.length === 0 ? (
                  <p className="px-5 py-4 text-sm text-gray-500">No clauses in this organization's library.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-800">
                          {["Title", "Category", "Kind", "Jurisdiction", "Source", "Updated"].map((h) => (
                            <th key={h} className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {clausesData.clauses.map((c) => (
                          <tr key={c.id} className="border-b border-gray-800/50 hover:bg-gray-800/20">
                            <td className="px-4 py-2.5 text-gray-200 max-w-xs truncate">{c.title}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-300 capitalize">{c.category.replace(/_/g, " ")}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-400 capitalize">{c.documentKind.replace("_", " ")}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-400">{c.jurisdiction ?? "—"}</td>
                            <td className="px-4 py-2.5">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.isSeed ? "bg-gray-700/50 text-gray-400 border border-gray-600/50" : "bg-blue-900/40 text-blue-300 border border-blue-700/50"}`}>
                                {c.isSeed ? "Starter" : "Custom"}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-gray-400">{fmtDate(c.updatedAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-800">
                <ScrollText className="h-4 w-4 text-gray-400" />
                <h2 className="text-sm font-semibold text-white">Recent Activity (last 20 entries)</h2>
              </div>
              {data.auditLog.length === 0 ? (
                <p className="px-5 py-4 text-sm text-gray-500">No audit log entries yet.</p>
              ) : (
                <div className="divide-y divide-gray-800/50">
                  {data.auditLog.map((e) => (
                    <div key={e.id} className="px-5 py-3 flex items-start gap-3 hover:bg-gray-800/20">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-semibold capitalize ${actionBadge(e.action)}`}>{e.action}</span>
                          <span className="text-xs text-gray-400 capitalize">{e.entityType.replace("_", " ")} #{e.entityId}</span>
                          {e.field && <span className="text-xs text-gray-500">· {e.field}</span>}
                        </div>
                        {e.actorName && <p className="text-xs text-gray-500 mt-0.5">by {e.actorName}</p>}
                        {e.oldValue != null && e.newValue != null && (
                          <p className="text-xs text-gray-600 mt-0.5 truncate">
                            <span className="line-through text-gray-600">{e.oldValue}</span>
                            {" → "}
                            <span className="text-gray-400">{e.newValue}</span>
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 flex-shrink-0 mt-0.5">{fmtDateTime(e.createdAt)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
